import { FC, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import { Box } from '@zoopla-eng/zds-components/box';
import { EditMedium } from '@zoopla-eng/zds-components/icons';
import { ButtonLeft } from '@zoopla-eng/zds-components/button';

import SpecifyRelationshipModal from '../SpecifyRelationshipModal/specify-relationship-modal';
import type { PropertyDataForAnalytics } from '@zoopla/analytics/property-data';
import { getTrackedRelationshipCopy } from '@zoopla/map-relationship';
import { getUPRNfromQuery } from '@zoopla/property-from-query';

import { useSaveRelationship } from '../hooks/useSaveRelationship';
import { styledErrorBannerStyle } from '../SpecifyRelationshipModal/specify-relationship-modal.styles.css';

type EditButtonProps = {
    analyticsData: PropertyDataForAnalytics;
};

const EditButton: FC<EditButtonProps> = ({ analyticsData }) => {
    const [isRelationshipModalShowing, updateIsRelationshipModalShowing] =
        useState(false);

    const {
        dismissRelationshipModal,
        saveAndCloseRelationshipModal,
        relationship,
        saveRelationshipLoading,
        saveRelationshipError
    } = useSaveRelationship(
        analyticsData,
        updateIsRelationshipModalShowing,
        true
    );

    const router = useRouter();
    const uprn = getUPRNfromQuery(router.query);
    const buttonRef = useRef<HTMLButtonElement>(null);

    return (
        <>
            <SpecifyRelationshipModal
                action={{
                    dismissRelationshipModal,
                    saveAndCloseRelationshipModal,
                    formShownEventLabel: 'edit relationship',
                    formShownEventCategory: 'tracked property details',
                    formShownEventValue: uprn ? parseInt(uprn, 10) : 0
                }}
                isShowing={isRelationshipModalShowing}
                isSubmissionLoading={saveRelationshipLoading}
                submissionError={saveRelationshipError}
                existingRelationship={relationship}
            />
            <Box
                width="100%"
                fw
                justifyContent="center"
                display="flex"
                pt="S100"
            >
                <ButtonLeft
                    category="tertiary"
                    Icon={EditMedium}
                    onClick={() => updateIsRelationshipModalShowing(true)}
                    data-testid="tracked-property-edit"
                    ref={buttonRef}
                    aria-label="Edit your relationship"
                >
                    {getTrackedRelationshipCopy(relationship)}
                </ButtonLeft>
            </Box>
            {saveRelationshipError && (
                <div
                    className={styledErrorBannerStyle}
                    data-testid="edit-relationship-error-banner"
                >
                    Something went wrong when updating your relationship to this
                    property. Please try again.
                </div>
            )}
        </>
    );
};

export default EditButton;
